import { useState, useEffect } from 'react';
import { axiosInstance } from '@/App';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Download, GripVertical } from 'lucide-react';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';

const CatalogGenerator = () => {
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [generating, setGenerating] = useState(false);
  
  // Available fields for export
  const availableFields = [
    { id: 'name', label: 'Name', enabled: true },
    { id: 'description', label: 'Description', enabled: true },
    { id: 'price', label: 'Price', enabled: true },
    { id: 'sku', label: 'SKU', enabled: true },
    { id: 'image_url', label: 'Main Image URL', enabled: true },
    { id: 'collection_name', label: 'Collection Name', enabled: true },
  ];
  
  const [fields, setFields] = useState(availableFields);

  useEffect(() => {
    fetchCategories();
    fetchProducts();
  }, []);

  const fetchCategories = async () => {
    try {
      const response = await axiosInstance.get('/categories');
      setCategories(response.data);
    } catch (error) {
      console.error('Failed to fetch categories:', error);
      toast.error('Failed to load categories');
    }
  };

  const fetchProducts = async () => {
    try {
      const response = await axiosInstance.get('/products');
      setProducts(response.data);
    } catch (error) {
      console.error('Failed to fetch products:', error);
      toast.error('Failed to load products');
    }
  };

  const handleCategoryToggle = (categoryId) => {
    setSelectedCategories(prev => 
      prev.includes(categoryId) 
        ? prev.filter(id => id !== categoryId)
        : [...prev, categoryId]
    );
  };

  const handleSelectAll = () => {
    if (selectedCategories.length === categories.length) {
      setSelectedCategories([]);
    } else {
      setSelectedCategories(categories.map(cat => cat.id));
    }
  };

  const handleFieldToggle = (fieldId) => {
    setFields(prev => prev.map(field => 
      field.id === fieldId ? { ...field, enabled: !field.enabled } : field
    ));
  };

  const handleDragEnd = (result) => {
    if (!result.destination) return;

    const items = Array.from(fields);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    setFields(items);
  };

  // Get all products for selected categories including subcategories
  const getProductsForCategory = (categoryId) => {
    const category = categories.find(cat => cat.id === categoryId);
    if (!category) return [];

    // Get all descendant category IDs
    const getAllDescendantIds = (cat) => {
      let ids = [cat.id];
      if (cat.children && cat.children.length > 0) {
        cat.children.forEach(child => {
          ids = [...ids, ...getAllDescendantIds(child)];
        });
      }
      return ids;
    };

    const categoryIds = getAllDescendantIds(category);
    
    // Get products that belong to any of these categories
    return products.filter(product => 
      product.categories && product.categories.some(catId => categoryIds.includes(catId))
    );
  };

  const handleGenerateCSV = () => {
    if (selectedCategories.length === 0) {
      toast.error('Please select at least one collection');
      return;
    }

    const enabledFields = fields.filter(f => f.enabled);
    if (enabledFields.length === 0) {
      toast.error('Please select at least one field to export');
      return;
    }

    setGenerating(true);

    try {
      let csvContent = '';

      // Process each selected category
      selectedCategories.forEach(categoryId => {
        const category = categories.find(cat => cat.id === categoryId);
        if (!category) return;

        // Add section header (category name in uppercase)
        csvContent += `${category.name.toUpperCase()}\n`;

        // Add column headers
        const headers = enabledFields.map(f => f.label).join(',');
        csvContent += headers + '\n';

        // Get products for this category (including subcategories)
        const categoryProducts = getProductsForCategory(categoryId);

        // Add product rows
        categoryProducts.forEach(product => {
          const row = enabledFields.map(field => {
            let value = '';
            
            switch (field.id) {
              case 'name':
                value = product.name || '';
                break;
              case 'description':
                value = product.description || '';
                break;
              case 'price':
                value = product.price || '';
                break;
              case 'sku':
                value = product.sku || '';
                break;
              case 'image_url':
                value = product.image_url || '';
                break;
              case 'collection_name':
                value = category.name || '';
                break;
              default:
                value = '';
            }

            // Escape commas and quotes in CSV
            if (typeof value === 'string') {
              value = value.replace(/"/g, '""'); // Escape quotes
              if (value.includes(',') || value.includes('"') || value.includes('\n')) {
                value = `"${value}"`; // Wrap in quotes if contains special chars
              }
            }

            return value;
          }).join(',');

          csvContent += row + '\n';
        });

        // Add blank line between sections
        csvContent += '\n';
      });

      // Create and download CSV file
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `product-export-${Date.now()}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      toast.success('CSV exported successfully!');
    } catch (error) {
      console.error('Failed to generate CSV:', error);
      toast.error('Failed to generate CSV export');
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div data-testid="catalog-generator-page">
      <div className="page-header">
        <h1 className="page-title">Product Export</h1>
        <p className="page-subtitle">Export products as CSV for data merge</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Collection Selection */}
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold">Select Collections</h2>
            <Button 
              variant="outline" 
              size="sm"
              onClick={handleSelectAll}
            >
              {selectedCategories.length === categories.length ? 'Deselect All' : 'Select All'}
            </Button>
          </div>
          
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {categories.length === 0 ? (
              <p className="text-slate-500 text-sm">No collections found</p>
            ) : (
              categories.map(category => (
                <label 
                  key={category.id} 
                  className="flex items-center gap-3 p-3 hover:bg-slate-50 rounded-lg cursor-pointer transition-colors"
                >
                  <input
                    type="checkbox"
                    checked={selectedCategories.includes(category.id)}
                    onChange={() => handleCategoryToggle(category.id)}
                    className="w-4 h-4 text-indigo-600 rounded border-slate-300 focus:ring-indigo-500"
                  />
                  <span className="text-sm font-medium text-slate-700">{category.name}</span>
                </label>
              ))
            )}
          </div>
          
          {selectedCategories.length > 0 && (
            <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-sm text-blue-800">
                <strong>{selectedCategories.length}</strong> collection{selectedCategories.length !== 1 ? 's' : ''} selected
              </p>
              <p className="text-xs text-blue-600 mt-1">
                Products from selected collections and their subcategories will be included
              </p>
            </div>
          )}
        </div>

        {/* Field Selection and Ordering */}
        <div className="card">
          <h2 className="text-xl font-semibold mb-4">Configure Export Fields</h2>
          
          <p className="text-sm text-slate-600 mb-4">
            Check fields to include and drag to reorder columns
          </p>
          
          <DragDropContext onDragEnd={handleDragEnd}>
            <Droppable droppableId="fields">
              {(provided) => (
                <div
                  {...provided.droppableProps}
                  ref={provided.innerRef}
                  className="space-y-2"
                >
                  {fields.map((field, index) => (
                    <Draggable key={field.id} draggableId={field.id} index={index}>
                      {(provided, snapshot) => (
                        <div
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          className={`flex items-center gap-3 p-3 bg-white border rounded-lg transition-all ${
                            snapshot.isDragging 
                              ? 'border-indigo-400 shadow-lg' 
                              : 'border-slate-200 hover:border-slate-300'
                          }`}
                        >
                          <div {...provided.dragHandleProps} className="cursor-grab active:cursor-grabbing">
                            <GripVertical className="w-5 h-5 text-slate-400" />
                          </div>
                          
                          <input
                            type="checkbox"
                            checked={field.enabled}
                            onChange={() => handleFieldToggle(field.id)}
                            className="w-4 h-4 text-indigo-600 rounded border-slate-300 focus:ring-indigo-500"
                          />
                          
                          <span className={`text-sm font-medium flex-1 ${
                            field.enabled ? 'text-slate-700' : 'text-slate-400'
                          }`}>
                            {field.label}
                          </span>
                          
                          <span className="text-xs text-slate-400">
                            #{index + 1}
                          </span>
                        </div>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>

          <div className="mt-6 p-4 bg-slate-50 border border-slate-200 rounded-lg">
            <h3 className="text-sm font-semibold text-slate-700 mb-2">CSV Structure:</h3>
            <ul className="text-xs text-slate-600 space-y-1">
              <li>• Section header for each collection (e.g., "BOWS")</li>
              <li>• Column headers based on selected fields</li>
              <li>• Products grouped by collection</li>
              <li>• Includes products from subcategories</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Generate Button */}
      <div className="mt-6 flex justify-end">
        <Button
          onClick={handleGenerateCSV}
          disabled={generating || selectedCategories.length === 0}
          size="lg"
          className="gap-2"
        >
          <Download className="w-5 h-5" />
          {generating ? 'Generating...' : 'Generate CSV Export'}
        </Button>
      </div>
    </div>
  );
};

export default CatalogGenerator;
